import {
  AsnEntry,
  CountryEntry,
  DuckDbFlowDescription,
  DuckDbResourceObject,
  FlowDetail,
  FlowMetric,
  FlowRessourceObject
} from "@app/netscope/model/data-models";
import {TransportProtocolDetail} from "@app/netscope/netscope-flows-datagrid/expandable-rows/expandable-rows.component";
import {util} from "@joint/core";
import uuid = util.uuid;


export class NetscopeUtils {

    static formatBytes(value, units = ["bytes", "KiB", "MiB", "GiB", "TiB"]) {
        if (value < 1024 || units.length === 1) {
            // Check if the number has decimals (https://stackoverflow.com/questions/2304052/check-if-a-number-has-a-decimal-place-is-a-whole-number)
            if (value % 1 === 0) {
                let unit = units[0];
                // Correct singular/plural mismatch (e.g., "1 bytes" → "1 byte")
                if (value == 1 && unit == "bytes") {
                    unit = "byte";
                }
                return `${value} ${unit}`;
            } else {
                return `${value.toFixed(2)} ${units[0]}`;
            }
        }

        return this.formatBytes(value / 1024, units.slice(1));
    }

    static getMax(arr) {
        return arr.reduce((max, v) => max >= v ? max : v, 0);
    }

    static getMin(arr) {
        return arr.reduce((min, v) => min <= v ? min : v, 0);
    }

    static _produceResourceObject(nodeMap: Map<string, FlowRessourceObject>, countryMap: Map<string, CountryEntry>, asnMap: Map<number, AsnEntry>, threatMode = false, obj_ipaddr, obj_uuid, obj_type, flow_resource_key, obj_asn_num, obj_country_code, duckDbNodeMapping: Map<string, DuckDbResourceObject>) {

      if (nodeMap.has(obj_uuid)) {
        return nodeMap.get(obj_uuid);
      }

      let resource_object: FlowRessourceObject;

        let shortUuid = obj_uuid == null ? `${obj_ipaddr}` : obj_uuid.split(":")[1];

        let name = shortUuid;
        let type = obj_type;
        let host = undefined;

        if (type == "external_ip") {
          name = `${obj_ipaddr} (${shortUuid})`;
        }

        if (duckDbNodeMapping.has(obj_uuid)) {
          let duckDbObject = duckDbNodeMapping.get(obj_uuid);
          name = duckDbObject.name;
          type = duckDbObject.type;

          if (duckDbObject.host != undefined) {
            host = duckDbNodeMapping.get(duckDbObject.host);
          }
        }

        resource_object = {
          uuid: obj_uuid,
          type: type,
          name: name,
          address: obj_ipaddr,
          countryInfo: countryMap.get(obj_country_code),
          country_code: obj_country_code,
          asnInfo: asnMap.get(obj_asn_num),
          nonInteractive: false,
          host: host,
          membersDisplayMode: "summarized"
        }

        nodeMap.set(obj_uuid, resource_object);

        return resource_object
    }

    static duckDbFlowsToFlowsDetails(duckDbFlows: DuckDbFlowDescription[], nodeMap: Map<string, FlowRessourceObject>, countryMap: Map<string, CountryEntry>, asnMap: Map<number, AsnEntry>, duckDbNodeMapping: Map<string, DuckDbResourceObject>, threatMode = false, skipMetrics=false): FlowDetail[] {
        let flowDetails = [];

        let flowMap: Map<string, FlowDetail> = new Map<string, FlowDetail>();

        for (let flow of duckDbFlows) {
            let params: [string, string, string, string, number, string][] = [
                [flow.src_ipaddr, flow.src_resource_uuid, flow.src_resource_type, "src", flow.src_asn_num, flow.src_country_code],
                [flow.dst_ipaddr, flow.dst_resource_uuid, flow.dst_resource_type, "dst", flow.dst_asn_num, flow.dst_country_code]
            ];
            for (let [obj_ipaddr, obj_uuid, obj_type, flow_resource_key, obj_asn_num, obj_country_code] of params) {
                let resource_object: FlowRessourceObject = NetscopeUtils._produceResourceObject(nodeMap, countryMap, asnMap, threatMode, obj_ipaddr, obj_uuid, obj_type, flow_resource_key, obj_asn_num, obj_country_code, duckDbNodeMapping);

                let attributeKey = `${flow_resource_key}_object`;
                flow[attributeKey] = resource_object;
            }

            // Ensure compatibility with FlowDetail (common flow datagrid)
            flow.source = flow.src_object;
            flow.target = flow.dst_object;

            // Add this flow to 'this.flowDetails'
            let flowKey = threatMode ? `${flow.source.uuid}__${flow.target.uuid}__${flow.port}` : `${flow.source.uuid}__${flow.target.uuid}`;

            if (!flowMap.has(flowKey)) {
                let newFlowDetail = new FlowDetail();

                newFlowDetail.src_ipaddr = flow.src_ipaddr;
                newFlowDetail.dst_ipaddr = flow.dst_ipaddr;

                if (nodeMap.has(flow.source.uuid)) {
                  newFlowDetail.source = nodeMap.get(flow.source.uuid);
                } else {
                    newFlowDetail.source = {
                        name: flow.source.name,
                        uuid: flow.source.uuid,
                        address: flow.source.address,
                        type: flow.source.type,
                        nonInteractive: false,
                        membersDisplayMode: "summarized"
                    };
                }

                if (nodeMap.has(flow.target.uuid)) {
                  newFlowDetail.target = nodeMap.get(flow.target.uuid);
                } else {
                    newFlowDetail.target = {
                        name: flow.target.name,
                        uuid: flow.target.uuid,
                        address: flow.target.address,
                        type: flow.target.type,
                        nonInteractive: false,
                        membersDisplayMode: "summarized"
                    };
                }

                newFlowDetail.exchanged_bytes = 0;
                newFlowDetail.exchanged_packets = 0;
                newFlowDetail.transport_protocols = [];
                flowDetails.push(newFlowDetail);
                flowMap.set(flowKey, newFlowDetail);
            }

            let flowDetail = flowMap.get(flowKey);
            flowDetail.exchanged_bytes += flow.exchanged_bytes;
            flowDetail.exchanged_packets += flow.exchanged_packets;

            if (! skipMetrics) {
              if (!flowDetail.metrics.has(flow.port)) {
                  let newFlowMetric = new FlowMetric();
                  newFlowMetric.port = flow.port;
                  newFlowMetric.direction = flow.direction;
                  newFlowMetric.application_protocol = flow.protocol;
                  newFlowMetric.transport_protocols = [];
                  newFlowMetric.exchanged_bytes = 0;
                  newFlowMetric.exchanged_packets = 0;
                  flowDetail.metrics.set(flow.port, newFlowMetric);
              }

              let flowMetric = flowDetail.metrics.get(flow.port);
              flowMetric.exchanged_bytes += flow.exchanged_bytes;
              flowMetric.exchanged_packets += flow.exchanged_packets;

              if (!flowMetric.transport_protocols.includes(flow.proto_raw)) {
                  flowMetric.transport_protocols.push(flow.proto_raw);
              }
            }

            if (threatMode) {
                // @ts-ignore
                flowDetail.threat = flow.threat;
                // @ts-ignore
                flowDetail.date = flow.date / 1000.0;
                // @ts-ignore
                flowDetail.threat.score = flow.score;
            }
        }

        return flowDetails;
    }

  static allCountriesCodes = [
    "fo", "um", "us", "jp", "sc", "in", "fr", "fm", "cn", "pt", "sw", "sh", "br", "ki", "ph", "mx", "es", "bu", "mv",
    "sp", "gb", "gr", "as", "dk", "gl", "gu", "mp", "pr", "vi", "ca", "st", "cv", "dm", "nl", "jm", "ws", "om", "vc",
    "tr", "bd", "lc", "nr", "no", "kn", "bh", "to", "fi", "id", "mu", "se", "tt", "my", "pa", "pw", "tv", "mh", "cl",
    "th", "gd", "ee", "ag", "tw", "bb", "it", "mt", "vu", "sg", "cy", "lk", "km", "fj", "ru", "va", "sm", "kz", "az",
    "tj", "ls", "uz", "ma", "co", "tl", "tz", "ar", "sa", "pk", "ye", "ae", "ke", "pe", "do", "ht", "pg", "ao", "kh",
    "vn", "mz", "cr", "bj", "ng", "ir", "sv", "sl", "gw", "hr", "bz", "za", "cf", "sd", "cd", "kw", "de", "be", "ie",
    "kp", "kr", "gy", "hn", "mm", "ga", "gq", "ni", "lv", "ug", "mw", "am", "sx", "tm", "zm", "nc", "mr", "dz", "lt",
    "et", "er", "gh", "si", "gt", "ba", "jo", "sy", "mc", "al", "uy", "cnm", "mn", "rw", "so", "bo", "cm", "cg", "eh",
    "rs", "me", "tg", "la", "af", "ua", "sk", "jk", "bg", "qa", "li", "at", "sz", "hu", "ro", "ne", "lu", "ad", "ci",
    "lr", "bn", "iq", "ge", "gm", "ch", "td", "kv", "lb", "dj", "bi", "sr", "il", "ml", "sn", "gn", "zw", "pl", "mk",
    "py", "by", "cz", "bf", "na", "ly", "tn", "bt", "md", "ss", "bw", "bs", "nz", "cu", "ec", "au", "ve", "sb", "mg",
    "is", "eg", "kg", "np"
  ];

    static base64ToArrayBuffer(base64) {
        const binaryString = atob(base64);

        const length = binaryString.length;
        const bytes = new Uint8Array(length);

        for (let i = 0; i < length; i++) {
            bytes[i] = binaryString.charCodeAt(i);
        }

        return bytes.buffer;
    }

    static detectType(uuid) {
        if (uuid !== undefined && uuid !== null) {
            if (uuid.indexOf("vim.VirtualMachine:") !== -1) {
                return "vm";
            }
            if (uuid.indexOf("vim.HostSystem:") !== -1) {
                return "host";
            }
            if (uuid.indexOf("xo.VirtualMachine:") !== -1) {
                return "vm";
            }
            if (uuid.indexOf("xo.HostSystem:") !== -1) {
                return "host";
            }
            if (uuid.indexOf("nu.VirtualMachine:") !== -1) {
                return "vm";
            }
            if (uuid.indexOf("nu.HostSystem:") !== -1) {
                return "host";
            }
            if (uuid.indexOf("vim.ExternalIpaddress:") !== -1) {
                return "external_ip";
            }
        }
        return "unknown_ip";
    }

    static trasnsportProtocolRawStringToObject(rawTransportProtocols: string[]): TransportProtocolDetail[] {
        let transport_protocols: TransportProtocolDetail[] = [];
        for (let transport of rawTransportProtocols) {
            let protoNumber = -1;
            let protoLabel = "unknown";
            let protoParts = transport.split(" ")
            if (protoParts.length == 2) {
                protoNumber = Number.parseInt(protoParts[0]);
                protoLabel = protoParts[1];
            }

            transport_protocols.push({
                protoNumber: protoNumber,
                protoLabel: protoLabel
            });
        }
        return transport_protocols;
    }

    static _cleanAsnUuid = (uuid) => {
        let result = uuid;
        if (result.includes('vim.asn:')) {
            result = result.replace('vim.asn:', '');
        }
        return result;
    }

    static flowsDetailsToPerPortFlowDetails(flowDetails: FlowDetail[]) {
        let sideFlowDetails: FlowDetail[] = [];

        for (let flowDetail of flowDetails) {

            // for (let link of relevantLinks) {
            for (let [port, metric] of flowDetail.metrics.entries()) {
                let clone: FlowDetail = flowDetail.clone();
                clone.exchanged_bytes = metric.exchanged_bytes;
                clone.exchanged_packets = metric.exchanged_packets;
                clone.port = metric.port;
                clone.direction = metric.direction;
                clone.protocol = metric.application_protocol;
                clone.transport_protocols = metric.transport_protocols;
                // relevantMetrics.push([clone, metric.direction]);
                sideFlowDetails.push(clone);
            }
            // }
        }
        return sideFlowDetails;
    }

    static safeLog(v) {
        if (v > 1.0) {
            return Math.log10(v);
        }
        return 0;
    }

    static exchangedBytesToColorHex = (exchanged_bytes, minBytes, maxBytes) => {
        let ratio = (NetscopeUtils.safeLog(exchanged_bytes) - NetscopeUtils.safeLog(minBytes)) / (NetscopeUtils.safeLog(maxBytes) - NetscopeUtils.safeLog(minBytes));
        let angle = Math.round((1.0 - ratio) * 120);
        let color = `hsl(${angle}, 100%, 50%)`;
        let s = 1.0;
        let l = 0.5;

        const k = (n: number) => (n + angle / 30) % 12;
        const a = s * Math.min(l, 1 - l);
        const f = (n: number) => l - a * Math.max(-1, Math.min(k(n) - 3, Math.min(9 - k(n), 1)));

        const toHex = (x: number) => Math.round(255 * x).toString(16).padStart(2, "0");

        return `#${toHex(f(0))}${toHex(f(8))}${toHex(f(4))}`;
    }

    static generateUuid = () => {
      let result = uuid().replaceAll("-", "__");
      return result
    }
}
